import { inactiveResearchEffect } from '../research-presentation';
/* eslint-disable @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unnecessary-type-assertion -- defensive reads of projection fields that may be absent on older bridges */
import { useState } from 'preact/hooks';
import { useStore } from '../store';
import type { UiResearchNode } from '../bridge';
import { ResearchGraph, humanize } from '../components/ResearchGraph';

/** Research panel (P612): active-study card + the ResearchGraph tree.
 *  Self-contained: reads the ui.mgmt.research projection (Sim-Core #326)
 *  through a narrow local cast until UI-Shell types the bridge field.
 *  Start dispatches research.start { studyId }. One study at a time (sim). */

export function ResearchPanel() {
  const { sim, bridge } = useStore();
  // UI-Shell bridge field (lane/ui-facility): null until the sim sends #326's shape.
  const r = sim.researchTree;
  const tree = r?.tree ?? [];
  const [openId, setOpenId] = useState<string | null>(null);
  const activeDef = r?.active ? tree.find((n) => n.id === r.active?.studyId) : undefined;
  const cash = sim.cashCents;
  const fmt = (cents: number) => (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  // Registry effect values: *_bp keys are basis points -> signed percent; anything else stays raw.
  const fmtEffect = (key: string, v: unknown): string => {
    if (key.endsWith('_bp') && typeof v === 'number') { const pctVal = v / 100; return `${pctVal >= 0 ? '+' : ''}${pctVal.toFixed(v % 100 === 0 ? 0 : 1)}%`; }
    return String(v);
  };

  const badge = (n: UiResearchNode) => {
    if (n.state === 'completed') return <span class="pill pill-amber">Done</span>;
    if (n.state === 'active') return <span class="pill">{r?.active ? `${r.active.remainingDays}d left` : 'Running'}</span>;
    if (n.state === 'locked') return <span class="pill" style="opacity:0.65">Locked</span>;
    if (n.state === 'excluded') return <span class="pill" style="opacity:0.65">Excluded</span>;
    return null;
  };

  const detail = (n: UiResearchNode) => {
    const afford = cash === undefined || n.costCents <= cash;
    const startable = n.state === 'available' && r?.active == null && afford;
    return (
      <div style="margin-top:8px">
        {(n.discountBp ?? 0) > 0 && n.baseCostCents !== undefined && n.state !== 'completed' && n.state !== 'active' && (
          <div class="row-label" style="text-transform:none;white-space:normal;margin-bottom:6px">
            Base cost ${fmt(n.baseCostCents)}. Existing one-shot discount {(n.discountBp ?? 0) / 100}%: next study charge ${fmt(n.costCents)}. The discount is consumed only when a study starts.
          </div>
        )}
        <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal">{n.description}</div>
        {n.effects != null && typeof n.effects === 'object' && Object.keys(n.effects as Record<string, unknown>).length > 0 && (
          <div style="margin-top:6px">
            {Object.entries(n.effects as Record<string, unknown>).map(([k, v]) => (
              <div key={k} class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal">- {humanize(k.replace(/_bp$/, ''))}: {fmtEffect(k, v)}{inactiveResearchEffect(k) ? ' - recorded only, not applied by the current game' : ''}</div>
            ))}
          </div>
        )}
        {n.effects != null && Object.keys(n.effects).some(inactiveResearchEffect) && <div class="row-label" style="margin-top:8px;white-space:normal;text-transform:none;letter-spacing:0.01em">This study includes recorded effects that are not applied yet. Its description names intended benefits, not active benefits for effects marked recorded only. Starting still charges the shown cost and uses the shown study time.</div>}
        {n.state === 'locked' && n.missingPrereqs.length > 0 && (
          <div class="row-label" style="margin-top:6px;color:var(--ui-amber, #b97a2a);text-transform:none;letter-spacing:0.01em;white-space:normal">
            Unlocks after: {n.missingPrereqs.join(', ')}
          </div>
        )}
        {n.state === 'excluded' && (n.excludedBy?.length ?? 0) > 0 && (
          <div class="row-label" style="margin-top:6px;color:var(--ui-amber, #b97a2a);text-transform:none;letter-spacing:0.01em;white-space:normal">
            Closed off by {n.excludedBy?.join(', ')} - the two lines can't both be studied.
          </div>
        )}
        {n.state === 'available' && (
          <button
            class="btn btn-quiet"
            style="margin-top:8px;width:100%"
            disabled={!startable}
            onClick={() => { bridge.send('research.start', { studyId: n.id }); }}
          >
            {r?.active != null ? 'A study is already running' : !afford ? `Need $${fmt(n.costCents)}` : `Start study - $${fmt(n.costCents)}`}
          </button>
        )}
      </div>
    );
  };

  return (
    <div style="padding:6px 14px 16px">
      <div class="eyebrow eyebrow-rule">Research</div>
      {r?.active && (
        <div class="card" style="margin-top:10px;padding:12px 16px">
          <div class="row-value" style="font-size:15px">{activeDef?.name ?? r.active.studyId}</div>
          <div class="row-label">
            {r.active.remainingDays > 0 ? `${r.active.remainingDays} days left - completes day ${r.active.completesDay}` : 'finishing'}
          </div>
          {activeDef && (
            <div class="progress" style="margin-top:8px">
              <div style={`width:${Math.min(100, Math.round(100 * (1 - r.active.remainingDays / Math.max(1, activeDef.durationDays))))}%`} />
            </div>
          )}
        </div>
      )}
      {tree.length === 0 && (
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:8px">
          The study catalog arrives with the research system.
        </p>
      )}
      <ResearchGraph tree={tree} activeStudyId={r?.active?.studyId ?? null} openId={openId} onToggle={(id) => { setOpenId(openId === id ? null : id); }} renderBadge={badge} renderDetail={detail} />
    </div>
  );
}
